import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_BIG_WINS, GAMES_WITH_ASSIGNMENT } from '../../graphql/games';
import { useTranslation } from 'react-i18next';
import useMainStore from '../../store/mainState';

const tabs = ['All Bets'];

// Generate realistic-looking bet data from big wins
const generateBetFromGame = (game: any) => {
    const usernames = ['Alex***', 'Mana***', 'Brow***', 'Th3m***', 'Sain***', 'John***', 'Star***', 'King***', 'Lucky***', 'Pro***'];
    const betAmount = (Math.random() * 20 + 0.1).toFixed(2);
    const multiplier = (Math.random() * 5).toFixed(2);
    const payout = (parseFloat(betAmount) * parseFloat(multiplier)).toFixed(2);
    const isWin = parseFloat(multiplier) >= 1;

    return {
        id: `${game.id}-${Date.now()}-${Math.random()}`,
        game: game.name,
        iconUrl: game.iconUrl,
        user: usernames[Math.floor(Math.random() * usernames.length)],
        betAmount,
        multiplier,
        payout,
        isWin,
    };
};

export const LiveBets = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('All Bets');
    const [bets, setBets] = useState<any[]>([]);
    const [isLive, setIsLive] = useState(true);
    const betsRef = useRef<any[]>([]);
    const { environment, labelData, user } = useMainStore();

    const { data: bigWinsData } = useQuery(GET_BIG_WINS, {
        variables: {
            categoryType: '',
            environmentId: environment?.id,
            labelId: +labelData?.id,
            first: 50,
            after: 0,
        },
        fetchPolicy: 'cache-and-network',
        skip: !labelData?.id || !environment?.id,
    });

    // Fetch multiple categories for variety — slots get the most
    const skipQuery = !labelData?.id || !environment?.id;
    const baseVars = { labelId: +labelData?.id, environmentId: environment?.id, isActive: true, after: 0 };
    const { data: slotsData } = useQuery(GAMES_WITH_ASSIGNMENT, {
        variables: { ...baseVars, first: 100, categoryType: 'SLOT' }, fetchPolicy: 'cache-and-network', skip: skipQuery,
    });
    const { data: slotsData2 } = useQuery(GAMES_WITH_ASSIGNMENT, {
        variables: { ...baseVars, first: 100, after: 100, categoryType: 'SLOT' }, fetchPolicy: 'cache-and-network', skip: skipQuery,
    });
    const { data: liveData } = useQuery(GAMES_WITH_ASSIGNMENT, {
        variables: { ...baseVars, first: 50, categoryType: 'LIVE' }, fetchPolicy: 'cache-and-network', skip: skipQuery,
    });
    const { data: pokerData } = useQuery(GAMES_WITH_ASSIGNMENT, {
        variables: { ...baseVars, first: 50, categoryType: 'POKER' }, fetchPolicy: 'cache-and-network', skip: skipQuery,
    });
    const { data: fishData } = useQuery(GAMES_WITH_ASSIGNMENT, {
        variables: { ...baseVars, first: 50, categoryType: 'FISH' }, fetchPolicy: 'cache-and-network', skip: skipQuery,
    });
    const { data: miniData } = useQuery(GAMES_WITH_ASSIGNMENT, {
        variables: { ...baseVars, first: 50, categoryType: 'MINI_GAME' }, fetchPolicy: 'cache-and-network', skip: skipQuery,
    });

    // Merge all games into one pool
    const allGamesRef = useRef<any[]>([]);
    useEffect(() => {
        const games = [
            ...(bigWinsData?.bigWins?.games || []),
            ...(slotsData?.gamesWithAssignment?.games || []),
            ...(slotsData2?.gamesWithAssignment?.games || []),
            ...(liveData?.gamesWithAssignment?.games || []),
            ...(pokerData?.gamesWithAssignment?.games || []),
            ...(fishData?.gamesWithAssignment?.games || []),
            ...(miniData?.gamesWithAssignment?.games || []),
        ];
        // Deduplicate by id
        const unique = Array.from(new Map(games.map((g: any) => [g.id, g])).values());
        if (unique.length > 0) {
            allGamesRef.current = unique;
        }
    }, [bigWinsData, slotsData, slotsData2, liveData, pokerData, fishData, miniData]);

    // Initialize bets when game pool is ready
    const hasInitialized = useRef(false);
    useEffect(() => {
        if (allGamesRef.current.length > 0 && !hasInitialized.current) {
            hasInitialized.current = true;
            const initialBets = Array.from({ length: 10 }, () => {
                const g = allGamesRef.current[Math.floor(Math.random() * allGamesRef.current.length)];
                return generateBetFromGame(g);
            });
            setBets(initialBets);
            betsRef.current = initialBets;
        }
    }, [bigWinsData, slotsData, slotsData2, liveData, pokerData, fishData, miniData]);

    // Simulate new bets coming in
    useEffect(() => {
        if (!isLive || !allGamesRef.current.length) return;

        const interval = setInterval(() => {
            const games = allGamesRef.current;
            const randomGame = games[Math.floor(Math.random() * games.length)];
            const newBet = generateBetFromGame(randomGame);

            betsRef.current = [newBet, ...betsRef.current.slice(0, 19)];
            setBets([...betsRef.current]);
        }, 600 + Math.random() * 1000);

        return () => clearInterval(interval);
    }, [isLive]);

    const filteredBets = activeTab === 'High Rollers'
        ? bets.filter(b => parseFloat(b.betAmount) > 5)
        : bets;

    return (
        <div className="mt-[30px]">
            <div className="flex items-center justify-between mb-[14px]">
                <div className="flex items-center gap-[12px]">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`text-[12px] lg:text-[14px] font-semibold px-[14px] py-[8px] rounded-[10px] transition-all duration-300 ${
                                activeTab === tab
                                    ? 'bg-[#0086D3] text-white'
                                    : 'text-white/40 hover:text-white/60'
                            }`}
                        >
                            <span className="text-[10px] font-bold text-[#4DBDFF] bg-[#4DBDFF]/10 px-[8px] py-[2px] rounded-[6px] uppercase tracking-wider">{t('mainScreen.allBets')}</span>
                        </button>
                    ))}
                </div>
                <button
                    onClick={() => setIsLive(!isLive)}
                    className="flex items-center gap-[8px]"
                >
                    <div className={`w-[8px] h-[8px] rounded-full ${isLive ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)] animate-pulse' : 'bg-white/20'}`}></div>
                    <span className="text-white/60 text-[13px] font-semibold">{t('mainScreen.liveBets')}</span>
                    <div className={`w-[40px] h-[22px] rounded-full p-[2px] transition-colors duration-300 cursor-pointer ${isLive ? 'bg-[#0086D3]' : 'bg-white/10'}`}>
                        <div className={`w-[18px] h-[18px] rounded-full bg-white transition-transform duration-300 ${isLive ? 'translate-x-[18px]' : 'translate-x-0'}`}></div>
                    </div>
                </button>
            </div>

            <div className="rounded-[16px] bg-white/[0.03] border border-white/[0.06] overflow-hidden">
                {/* Header */}
                <div className="grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr] px-[16px] py-[12px] border-b border-white/[0.06]">
                    <span className="text-white/30 text-[12px] font-semibold">{t('mainScreen.game')}</span>
                    <span className="text-white/30 text-[12px] font-semibold">{t('mainScreen.user')}</span>
                    <span className="text-white/30 text-[12px] font-semibold text-center">{t('mainScreen.betAmount')}</span>
                    <span className="text-white/30 text-[12px] font-semibold text-center">{t('mainScreen.multiplier')}</span>
                    <span className="text-white/30 text-[12px] font-semibold text-right">{t('mainScreen.payout')}</span>
                </div>

                {/* Rows */}
                <div className="max-h-[400px] overflow-hidden">
                    {filteredBets.map((bet, index) => (
                        <div
                            key={bet.id}
                            className={`grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr] px-[16px] py-[10px] items-center border-b border-white/[0.03] transition-all duration-500 ${
                                index === 0 ? 'animate-[slideDown_0.4s_ease-out] bg-white/[0.03]' : ''
                            } ${index % 2 === 0 ? 'bg-white/[0.015]' : ''}`}
                        >
                            <div className="flex items-center gap-[8px]">
                                <img src={bet.iconUrl} alt="" className="w-[28px] h-[28px] rounded-[6px] object-cover" />
                                <span className="text-white text-[13px] font-medium truncate">{bet.game}</span>
                            </div>
                            <span className="text-white/50 text-[13px] font-medium truncate">{bet.user}</span>
                            <span className="text-white/70 text-[13px] font-semibold text-center">${bet.betAmount}</span>
                            <span className="text-white/50 text-[13px] font-medium text-center">{bet.multiplier}x</span>
                            <div className="flex items-center justify-end gap-[6px]">
                                <span className={`text-[13px] font-bold ${bet.isWin ? 'text-emerald-400' : 'text-white/30'}`}>
                                    ${bet.payout}
                                </span>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={bet.isWin ? '#34d399' : '#ffffff30'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d={bet.isWin ? 'M12 19V5M5 12l7-7 7 7' : 'M12 5v14M5 12l7 7 7-7'} />
                                </svg>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <style>{`
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};
